import {
	Icon,
	Modal,
	Typography
} from '@apostrophe/ui';
import {
	canChange,
	dollarFormat,
	get83DaysAheadDate,
	getAROrderChangeInitialDate
} from 'helpers/checkoutHelpers';
import { triggerCartEvent } from 'helpers/dataLayerHelpers';
import {
	get,
	isEmpty,
} from 'lodash';
import PropTypes from 'prop-types';
import React, {
	useRef, useState
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { CheckBoxToggle } from 'styledComponents/Inputs';
import { breakpoints } from 'styleUtils';

import {
	ARDetailsContainer,
	ARDetailsSubTitle,
	ARDetailsTextContainer,
	ARDetailsWrapper,
	ARModal,
	Cancel,
	InfoAndThumbnailWrapper,
	InfoBox,
	InfoModal,
	InfoWrapper,
	NewCheckoutAutoRefillContainer,
	NewCheckoutAutoRefillDetail,
	NewCheckoutContainer,
	NewCheckoutPrice,
	NewCheckoutSingleOptionCard,
	NewCheckoutTitleAndPrice,
	NewCheckoutToggleContainer,
	PricingContainer,
	PricingWrapperDesktop,
	PricingWrapperMobile,
	PrimaryInfoContainer,
	ProductOptionsSelect,
	StatusTag,
	ThumbnailWrapper,
	ThumbnailWrapperDesktop
} from '../styles';

const getBestPricedOption = (multiOptions) => {
	let bestPricedOption, bestMonthlyPrice
	multiOptions?.forEach(o => {
		let monthlyPrice = (o.autorefill?.price_cents || o.price_cents) / o.months
		if (!bestPricedOption || ( monthlyPrice < bestMonthlyPrice)) {
			bestPricedOption = o;
			bestMonthlyPrice = monthlyPrice;
		}
	})
	return bestPricedOption
}

async function handleRemoveProduct(remove, cart, setAutoShip, removeProduct, product) {
	if (remove) {
		if (cart.products[product.id]) {
			// default auto-refill option
			setAutoShip(product.id,
				true);
			// log amplitude event when an item is removed from the cart
			logAmplitudeEvent('Click:Checkout_Page',
				{ Checkout_click_name: 'remove' })
		}
		removeProduct(product.id);
	}
	// Handle GA4 event push for either a removal or an add
	triggerCartEvent(product,
		remove);
}

const PricingComponent = (product, autoRefill, multiOptions, otcCheckout, option, cart, notInCart, setAutoShip, removeProduct) => {
	function formatPrice() {
		const price = option?.price_cents / 100;

		const {
			disabled, options,
		} = product;
		if (disabled) {
			return null;
		}

		if (!isEmpty(autoRefill) && autoRefill.selected) {
			if (autoRefill.price_cents) {
				return (
					<NewCheckoutPrice className="flex">
						<span className="line-through">{dollarFormat(options[0].price_cents)}</span>
						<strong>{dollarFormat(autoRefill.price_cents)}</strong>
					</NewCheckoutPrice>
				)
			}
			// For no refill items like hydroquinone
			return (
				<NewCheckoutPrice className="flex">
					<strong>{dollarFormat(options[0].price_cents)}</strong>
				</NewCheckoutPrice>
			)
		}
		if (multiOptions.length > 0) {
			let worstPrice = 0;
			multiOptions.forEach(option => {
				if (!option) {
					return
				}
				let monthlyPrice = option.price_cents / option.months / 100
				if (monthlyPrice > worstPrice) {
					worstPrice = monthlyPrice
				}
			})

			const currentPrice = autoRefill?.selected ? autoRefill.price_cents / 100 : price

			return (
				<NewCheckoutPrice className="flex">
					{worstPrice > currentPrice / option.months && <span className="line-through">${worstPrice * option.months}</span>}
					<strong>${currentPrice}</strong>
				</NewCheckoutPrice>
			);
		}

		return (
			<NewCheckoutPrice className="flex"><strong>{dollarFormat(options[0].price_cents)}</strong></NewCheckoutPrice>
		)
	}

	return (<>
		<PricingContainer>
  	{formatPrice()}
			{product.duration >= 30 && (!option || option?.months > 1) &&
      <div className="description-price">
        ${(!isEmpty(autoRefill) && autoRefill.selected) ? (autoRefill.price_cents ? autoRefill.price_cents / 300 : product.options[0].price_cents / 300) : product.options[0].price_cents / (100 * (product.medication_type === 'topical' ? 3 : option?.months ? option.months : (product.options[0].price_cents.months ? product.options[0].price_cents.months : 1)))} a month {notInCart && !product.medication_type === 'topical' && ' with Auto Refill'}
      </div>}
		</PricingContainer>
		{!otcCheckout && cart.products[product.id] &&
      <Icon className="remove-icon" name='trash' size='m' role='button' tabIndex='0' aria-label='remove item from cart' onClick={() => handleRemoveProduct(true,
      	cart,
      	setAutoShip,
      	removeProduct,
      	product)} />
		}
	</>)

}

const SingleOption = ({
	cart,
	product,
	changeOption,
	removeProduct,
	setAutoShip,
	notInCart,
	index,
	otcCheckout,
	invoice,
}) => {
	const autoRefill = get(product,
		'options[0].autorefill',
		{});
	// determines if an item has auto-refill already enabled.
	const isAutoRefill = get(product,
		'autorefill.status',
		'') === 'UPCOMING';
	const threeMonthDate = get83DaysAheadDate();
	const AROrderChangeInitialDate = getAROrderChangeInitialDate();
	const [info, setInfo] = useState(null); // one of: null (modal closed), "mobile" (use modal component), or "desktop" (use tooltip)
	const isDesktop = useMediaQuery({ query: `(min-width: ${breakpoints.desktop}px)` });
	const rxType = get(product,
		'rx_type',
		'');
	const dosage = get(product,
		'dosage',
		'').replace(/\s+/g,
		'');
	const isBodyFormula = rxType && rxType.includes('BODY_FORMULA');

	const getProductTitle = () => {
		let productName = '';
		if (product.medication_type === 'topical' && !isBodyFormula) productName = 'Custom topical treatment';
		else productName = `${isBodyFormula ? 'Body - ' : ''} ${product.descriptor_short} ${product.medication_type} ${product.medication_form}`
		return productName;
	}
	// showARModal toggles AR modal that shows on hover of disabled AR checkbox (AR checkbox is disabled when AR is already enabled for the item.)
	const [showARModal, setShowARModal] = useState(false);
	// a medication is on hold if there an active auto-refill associated with it and patient has submitted a consultation that is under review.
	const isMedicationOnHold = invoice?.length > 0;
	// Double-checking that we are only showing 3 months
	// Too many places hard-coded for 3 month ar to allow multiple
	let multiOptions = product.options.length > 1 ? product.options.map(o => {
		if (o.autorefill && o.months !== 3) {
			delete o.autorefill
		}
		return o
	}) : [];
	const [bestPricedOption, setBestPricedOption] = useState(getBestPricedOption(multiOptions))

	// Only allow 1 month + best price unless on autorefill
	multiOptions = multiOptions.filter(o => o.id === bestPricedOption?.id || (!bestPricedOption?.autorefill && o.months === 1))

	const [option, setOption] = useState(bestPricedOption);
	const [optionSavings, setOptionSavings] = useState(0);

	const select = useRef();
	const price = option?.price_cents / 100;

	const selectOption = option_id => {
		let optionIdx = multiOptions.findIndex(o => o.id == option_id)
		optionIdx = optionIdx > 0 ? optionIdx : 0;
		const selectedOption = multiOptions[optionIdx];
		if (selectedOption.months == 1) {
			const bestPriceMonthlyCost = bestPricedOption.price_cents / bestPricedOption.months;
			const difference = selectedOption.price_cents - bestPriceMonthlyCost;
			setOptionSavings(difference);
		}
		changeOption(product.id,
			multiOptions[optionIdx]);
		setOption(multiOptions[optionIdx])

	};

	const filteredOptions = multiOptions.filter(o => !autoRefill?.selected || !isEmpty(o.autorefill))

	// determines which items should have AR checkbox selected by default.
	if (notInCart && autoRefill && product.medication_type === 'topical') {
		autoRefill.selected = true;
	}

	const ARModalInfoText = [
		{
			title: 'Ships and bills every 3 months',
			description: `Your order today should arrive within 3-5 business days. Your next auto-refill order will bill and ship on ${threeMonthDate} and follow a 3-month billing and shipment cycle.`,
		},
		{
			title: 'Change or cancel anytime',
			description: `You may change your shipping date or cancel your auto-refill order up until ${AROrderChangeInitialDate} (48 hours before your next shipment). We'll send a reminder email 6 days before shipping.`,
		},
		{
			title: `${autoRefill && autoRefill.discount_cents ? `Get $${autoRefill.discount_cents / 100 + 20} savings ` : 'Save '}every quarter`,
			description: `${autoRefill && autoRefill.discount_cents ? `$${autoRefill.discount_cents / 100} discount + FREE follow-up visits ($20 value) - that's $${(autoRefill.discount_cents * 4 / 100) + 80} in savings every year!` : 'Prescription discount + FREE follow-up visits ($20 value)'}`,
		},
		{
			title: 'Treatment that changes with you',
			description: 'Based on your skin\'s progress, your provider may adjust your treatment plan throughout your journey for best results. These will update automatically and ship on your auto-refill schedule.',
		},
	]

	const handleOpenOrCloseInfo = (isOpen) => {
		if (isAutoRefill) return;
		if (isOpen) {
			if (isDesktop) setInfo('desktop');
			else setInfo('mobile');
		}
		else {
			setInfo(null);
		}
	}

	const ARModalInfoComponent = () => {
		return (
			<>
				{ARModalInfoText.map(line => (
					<div key={line.title}>
						<Typography variant='body2' className="info-box-title">{line.title}</Typography>
						<Typography variant='body2' className="info-box-description">{line.description}</Typography>
					</div>
				))}
			</>
		)
	}

	// show AR Modal on mouse hover of disabled checkbox to inform pt why it is unselectable.
	const handleMouseOver = () => {
		if (isAutoRefill && !info) {
			setShowARModal(true);
		}
	}

	function handleChange() {
		// if auto refill is not already selected and an action was taken on the checkbox we can assume item was checked
		const event_value = !autoRefill.selected ? 'Check_AR' : 'Uncheck_AR';
		logAmplitudeEvent('Click:Checkout_Page',
			{ Checkout_click_name: event_value })

		setAutoShip(product.id,
			!autoRefill.selected);
	}

	// Hide unpurchaseable products
	if (isEmpty(autoRefill) && (!product.options || !product.options.length)) {
		return null;
	}

	return (
		<article>
			<NewCheckoutContainer notInCart={notInCart} data-cy={`NewCheckoutContainer_${rxType}_${dosage}`}>
				<NewCheckoutSingleOptionCard
					disabled={product.disabled}
					index={index}
					selected={cart.products[product.id]}
					onClick={() => canChange(cart,
						product) ? changeOption(product.id,
							product.options[0]) : null}
					data-cy={'NewCheckoutSingleOptionCard'}
				>
					{info && info == 'mobile' &&
						<Modal
							open={info && info == 'mobile'}
							onClose={() => handleOpenOrCloseInfo(false)}
							size="medium"
							role='dialog'
							aria-modal='false' // signifies no interaction
							tabIndex='0'
							dataDialog={true}
						>
							<InfoModal>
								{ARModalInfoComponent()}
							</InfoModal>
						</Modal>
					}
					{info && info == 'desktop' &&
						<InfoBox
							onClick={() => handleOpenOrCloseInfo(false)}
							role='dialog'
							aria-modal='false' // signifies no interaction
							tabIndex='0'
							dataDialog={true}
						>
							<div style={{ position: 'relative' }}>
								<Cancel top={-8} right={-8} />
								{ARModalInfoComponent()}
							</div>
						</InfoBox>}
					<NewCheckoutTitleAndPrice data-cy={`NewCheckoutTitleAndPrice_${rxType}_${dosage}`}>
						<InfoAndThumbnailWrapper>
							<InfoWrapper>
								<div className="title">
									{autoRefill?.selected &&
									<StatusTag>
										<span>AUTO-REFILL</span>
									</StatusTag>}
									<Typography className="product-title" variant='body2'>{getProductTitle()}{!product.otc_id && <span className="rx-label">RX</span>}</Typography>
									{product.medication_type === 'topical' && !isBodyFormula && <p className='description'>Formula: {product.drugname}</p>}
									<div className="description" data-cy={`CheckoutMedicationAmount_${rxType}_${dosage}`}>
										{product.medication_type === 'topical' ? `Size: ${product.dispense_quantity} grams` : `Qty: ${product.options && product.options[0].quantity} ${product.medication_form}`}
									</div>
								</div>
								<PricingWrapperDesktop>
									{PricingComponent(product,
										autoRefill,
										multiOptions,
										otcCheckout,
										option,
										cart,
										notInCart,
										setAutoShip,
										removeProduct )}
								</PricingWrapperDesktop>
							</InfoWrapper>
							<ThumbnailWrapper type={isBodyFormula ? 'body' : product.medication_type}>
								<span role='img' aria-label='product photo'></span>
							</ThumbnailWrapper>
						</InfoAndThumbnailWrapper>
						<PricingWrapperMobile>
							{PricingComponent(product,
								autoRefill,
								multiOptions,
								otcCheckout,
								option,
								cart,
								notInCart,
								setAutoShip,
								removeProduct)}
						</PricingWrapperMobile>
						<PrimaryInfoContainer>
							<ThumbnailWrapperDesktop type={isBodyFormula ? 'body' : product.medication_type}>
								<span role='img' aria-label='product photo'></span>
							</ThumbnailWrapperDesktop>
							{!isEmpty(autoRefill) && !notInCart &&
								<ARDetailsWrapper>
									<ARDetailsContainer>
										<ARDetailsTextContainer>
											<NewCheckoutAutoRefillContainer data-cy={`NewCheckoutAutoRefillContainer_${rxType}_${dosage}`} onMouseOver={handleMouseOver}>
												{showARModal && <ARModal onMouseLeave={() => setShowARModal(false)}>
													<Cancel onClick={() => setShowARModal(false)} />
												This item is already on auto-refill. To manage an order, visit your <a href='/yd/patient/order_history/'>Orders</a> page.
												</ARModal>}
												<NewCheckoutToggleContainer disabled={isAutoRefill} data-cy={`NewCheckoutToggleContainer_${rxType}_${dosage}`}>
													<span data-cy={`CheckBoxToggle_${rxType}_${dosage}`} role='checkbox' tabIndex='0' className='ar-detail-checkbox' name='turn on auto-refill checkbox' id={`${rxType}_${dosage}`}>
														<CheckBoxToggle
															checked={autoRefill.selected}
															onChange={() => handleChange(product.id)}
															color={!isAutoRefill ? 'darkGrey' : '#C2C2C2'}
															outline={autoRefill.selected ? false : true}
															id={product.id}
															data-testid={product.id}
															medCheckout={true}
															disabled={isAutoRefill} /* if item is already on auto refill, the AR option cannot be unselected. */
															type='checkbox'
														/>
													</span>
													<label htmlFor={`${rxType}_${dosage} auto refill checkbox`} >
														<Typography variant='body2' className="selected">
															{`Auto-refill & save ${dollarFormat(autoRefill.discount_cents)}`}
														</Typography>
													</label>
												</NewCheckoutToggleContainer>
											</NewCheckoutAutoRefillContainer>
											{autoRefill.selected ? (
												(!isMedicationOnHold && <NewCheckoutAutoRefillDetail autoRefill={autoRefill.selected} data-cy={'NewCheckoutAutoRefillDetail'}>
													<Typography variant='body2' weight='light'>{isAutoRefill ? 'This item is setup to auto-refill. Ships and bills every 3 months.' : 'Ships and bills every 3 months. Cancel anytime.'}</Typography>
												</NewCheckoutAutoRefillDetail>)
											) : (
												<NewCheckoutAutoRefillDetail autoRefill={autoRefill.selected} data-cy={'NewCheckoutAutoRefillDetail'}>
													<Typography variant='body2' weight='light'>Ships and bills every 3 months. Cancel anytime.</Typography>
												</NewCheckoutAutoRefillDetail>
											)}
										</ARDetailsTextContainer>
										<div
											className='question-icon-wrapper'
											role='button'
											tabIndex='0'
											aria-label='details'
											aria-haspopup='dialog'
											aria-expanded={info == 'desktop' || info == 'mobile'}
											onClick={() => handleOpenOrCloseInfo(true)}
										>
											<Icon name='question-circle' size='s' />
										</div>
									</ARDetailsContainer>
									{autoRefill.selected && !isAutoRefill &&
										<ARDetailsSubTitle>
											<Icon name='rotate' size='s'/>
											<Typography variant="body1">Delivery every 3 months</Typography>
										</ARDetailsSubTitle>
									}
								</ARDetailsWrapper>
							}
							{!notInCart && filteredOptions.length > 1 && <ProductOptionsSelect oral={true} autoRefill={!isEmpty(option.autoRefill)} data-cy={'ProductOptionsSelect'}>
								<div>
									<select
										onChange={({ target: { value } }) => selectOption(value)}
										ref={select}
										data-cy={`SupplySelect_${rxType}_${dosage}`}
										value={option.id}
									>
										{filteredOptions.slice().reverse().map((o) => {
											return (
												<option
													className="option"
													key={o.id}
													value={o.id}
												>
													{o.months ? `${o.months} month supply` : '1 month supply'}
												</option>
											)
										})}
									</select>
									{option.months === 1 && <Typography variant="cta" className='option-sub-text'>{`Save ${dollarFormat(optionSavings)} a month with a 3 month supply`}</Typography>}
								</div>
								<Icon name='chevron-down' size='m' />
							</ProductOptionsSelect>}
						</PrimaryInfoContainer>

					</NewCheckoutTitleAndPrice>
				</NewCheckoutSingleOptionCard>
			</NewCheckoutContainer>
		</article>
	);
};

export default SingleOption;

const {
	shape, arrayOf, object, bool, string, array, func, number, oneOfType,
} = PropTypes;
SingleOption.propTypes = {
	cart: PropTypes.shape({
		products: object,
		followup_allowed: bool,
	}).isRequired,
	changeOption: func.isRequired,
	removeProduct: func.isRequired,
	product: shape({
		id: string.isRequired,
		disabled: bool,
		options: arrayOf(
			shape({
				autorefill: shape({
					selected: bool,
					price_cents: number,
					discount_cents: number,
				}),
			}),
		).isRequired,
		descriptor_short: string.isRequired,
		medication_type: oneOfType([bool, string]).isRequired,
		medication_form: string.isRequired,
		quantity_remaining: number.isRequired,
		pending_request: bool.isRequired,
		shipments: array,
	}).isRequired,
};
